import { useContext, useState, useEffect, useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { SidebarContext } from "../../context/sidebarContext";
import { useGetCategoryQuery } from "../../slices/assetApiSlice";
import logo from "../../assets/images/minimized-wordless.svg";
import { ChevronDown } from "lucide-react";
import "./Sidebar.css";

const Sidebar = ({ navigationLinks, roleBasePath }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isSidebarOpen, toggleSidebar } = useContext(SidebarContext);
  const { data: categories } = useGetCategoryQuery();
  const [deviceType, setDeviceType] = useState("desktop");
  const [openDropdowns, setOpenDropdowns] = useState({});

  // Determine device type
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setDeviceType("mobile");
      } else if (width < 1024) {
        setDeviceType("tablet");
      } else {
        setDeviceType("desktop");
      }
    };

    handleResize(); // Set initial device type
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Function to handle navigation clicks
  const handleNavClick = () => {
    // Close sidebar on mobile when nav item is clicked
    if (deviceType === "mobile" && isSidebarOpen) {
      toggleSidebar();
    }
  };

  // Function to toggle dropdown
  const toggleDropdown = (linkId) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [linkId]: !prev[linkId],
    }));
  };

  // Get sidebar classes based on device type and state
  const getSidebarClasses = () => {
    const baseClasses =
      "bg-card border-r border-border transition-all duration-300 ease-in-out flex flex-col h-screen overflow-hidden flex-shrink-0";

    if (deviceType === "mobile") {
      // Mobile: overlay when open, hidden when closed
      return `${baseClasses} ${
        isSidebarOpen
          ? "fixed w-72 z-[1000] shadow-lg transform translate-x-0"
          : "fixed w-72 z-[1000] transform -translate-x-full"
      }`;
    } else if (deviceType === "tablet") {
      // Tablet: push content when open, show icons when closed
      return `${baseClasses} ${isSidebarOpen ? "w-72" : "w-16"}`;
    } else {
      // Desktop: full width when open, icons when closed
      return `${baseClasses} ${isSidebarOpen ? "w-72" : "w-16"}`;
    }
  };

  // Filter navigation links based on device type and state
  const enrichedNavigationLinks = useMemo(() => {
    return navigationLinks.map((link) => {
      if (link.title === "Assets" && link.submenu) {
        const existingPaths = link.submenu.map((s) =>
          s.path.split("/").pop().toLowerCase(),
        );
        const dynamicSubmenu = categories
          ?.filter((cat) => !cat.deleted && cat.name.toLowerCase() !== "other")
          .filter((cat) => !existingPaths.includes(cat.name.toLowerCase()))
          .map((cat) => ({
            id: `dynamic-${cat.id}`,
            title: cat.name,
            path: `assets/${cat.name.toLowerCase().replace(/\s+/g, "-")}`,
          }));

        return {
          ...link,
          submenu: [...link.submenu, ...(dynamicSubmenu || [])],
        };
      }
      return link;
    });
  }, [navigationLinks, categories]);

  return (
    <>
      {/* Mobile Backdrop Overlay - Show only on mobile when sidebar is open */}
      {deviceType === "mobile" && (
        <div
          className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-[999] transition-all duration-300 ${
            isSidebarOpen ? "opacity-100 visible" : "opacity-0 invisible"
          }`}
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <div className={getSidebarClasses()}>
        {/* Logo Section */}
        <div className="flex pl-4 items-center h-16 border-b border-border">
          <div className="overflow-hidden transition-all h-full flex gap-2 items-center">
            <img src={logo} alt="profile" className="h-10 object-contain" />
            <div
              className={`flex flex-col transition-all duration-300 ${
                isSidebarOpen ? "opacity-100 max-w-none" : "opacity-0 max-w-0"
              }`}
            >
              <span className="font-semibold text-base tracking-tight leading-tight whitespace-nowrap text-foreground">
                NHDCL
              </span>
              <span className="text-xs leading-tight whitespace-nowrap text-muted-foreground">
                Facility  Management System
              </span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 flex flex-col overflow-y-auto overflow-x-hidden custom-scrollbar">
          <ul className="list-none space-y-1">
            {enrichedNavigationLinks.map((link) => {
              const IconComponent = link.icon;
              const linkPath =
                link.path === ""
                  ? `/${roleBasePath}`
                  : `/${roleBasePath}/${link.path}`;
              const isHomePath = link.path === "";
              const hasSubmenu = link.submenu && link.submenu.length > 0;
              const isDropdownOpen = openDropdowns[link.id];

              return (
                <li
                  className="relative transition-all duration-200 w-full"
                  key={link.id}
                >
                  {hasSubmenu ? (
                    // Parent item with submenu
                    <>
                      <button
                        onClick={() => {
                          toggleDropdown(link.id);
                          if (link.path) {
                            navigate(`/${roleBasePath}/${link.path}`);
                          }
                        }}
                        className={`h-10 rounded-md flex items-center tracking-wide transition-all duration-200 hover:bg-accent hover:text-accent-foreground w-full ${
                          isSidebarOpen
                            ? "justify-between px-3 gap-3"
                            : "justify-center w-10 mx-auto"
                        } text-muted-foreground`}
                      >
                         <div className="flex items-center gap-3 min-w-0">
                           <IconComponent className="w-5 h-5 flex-shrink-0 transition-all duration-200" />
                           <span
                             className={`text-sm transition-all duration-200 whitespace-nowrap truncate ${
                               isSidebarOpen
                                 ? "opacity-100 max-w-none ml-0"
                                 : "opacity-0 max-w-0 ml-0"
                             }`}
                           >
                             {link.title}
                           </span>
                         </div>
                         {isSidebarOpen && (
                           <ChevronDown
                             className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${
                               isDropdownOpen ? "rotate-180" : ""
                             }`}
                           />
                         )}
                      </button>
                      {/* Submenu items */}
                      {isSidebarOpen && isDropdownOpen && (
                        <ul className="mt-1 ml-4 space-y-1 border-l-2 border-border pl-3">
                          {link.submenu.map((sublink) => {
                            const sublinkPath = `/${roleBasePath}/${sublink.path}`;
                            return (
                              <li key={sublink.id}>
                                <NavLink
                                  to={sublinkPath}
                                  onClick={handleNavClick}
                                  className={() => {
                                    const isActive = 
                                      location.pathname + location.search === sublinkPath ||
                                      (location.pathname === sublinkPath && location.search === "");
                                      
                                    return `h-9 rounded-md flex items-center tracking-wide transition-all duration-200 hover:bg-accent hover:text-accent-foreground px-3 text-sm ${
                                      isActive
                                        ? "bg-accent text-accent-foreground font-medium border-l-2 border-[#305845] rounded-l-none hover:pl-4"
                                        : "text-muted-foreground"
                                    }`;
                                  }}
                                >
                                  {sublink.title}
                                </NavLink>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </>
                  ) : (
                    // Regular navigation item without submenu
                    <NavLink
                      to={linkPath}
                      onClick={handleNavClick}
                      className={({ isActive }) => {
                        // Custom logic for home path
                        let finalIsActive = isActive;
                        if (isHomePath) {
                          const currentPath = window.location.pathname;
                          finalIsActive =
                            currentPath === `/${roleBasePath}` ||
                            currentPath === `/${roleBasePath}/`;
                        }

                        return `h-10 rounded-md flex items-center tracking-wide transition-all duration-200 hover:bg-accent hover:text-accent-foreground ${
                          isSidebarOpen
                            ? "justify-start px-3 gap-3"
                            : "justify-center w-10 mx-auto"
                        } ${
                          finalIsActive
                            ? "bg-accent text-accent-foreground font-medium"
                            : "text-muted-foreground"
                        }`;
                      }}
                      end={!isHomePath}
                    >
                      <IconComponent className="w-5 h-5 flex-shrink-0 transition-all duration-200" />
                      <span
                        className={`text-sm transition-all duration-200 whitespace-nowrap ${
                          isSidebarOpen
                            ? "opacity-100 max-w-none ml-0"
                            : "opacity-0 max-w-0 ml-0"
                        }`}
                      >
                        {link.title}
                      </span>
                    </NavLink>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
